import { Inject, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConfirmationMessageModalComponent } from '@components/modals/confirmation-message-modal/confirmation-message-modal.component';
import { MOBILE_APP_CALLBACK_URL, MOBILE_APP_REFERRAL, MOBILE_APP_SCHEME, MODAL_DIALOG_CENTER } from '@constants/app.constants';
import { WINDOW } from '@constants/global-objects.constants';
import { Booking } from '@models/booking.models';
import { AppCopy } from '@models/cms/app.model';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { selectAppId } from '@selectors/app.selector';
import { selectBooking } from '@selectors/booking.selector';
import { selectAppCmsCopy } from '@selectors/cms.selector';
import {
  selectIsFlightsPage,
  selectIsFlowOrTripsPage,
} from '@selectors/router.selector';
import { AppInsightsService } from '@services/app-insights/app-insights.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { ModalService } from '@services/modal/modal.service';
import { WindowService } from '@services/window/window.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Observable, Subject, take } from 'rxjs';

interface NotificationModalConfig {
  confirmAction?: () => void;
  confirmBtnText: string;
  message: string;
  title: string;
}

@Injectable({
  providedIn: 'root',
})
export class MultiAppService {
  private store = inject(Store);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private gbs = inject(GrowthBookService);
  private modalService = inject(ModalService);
  private appInsights = inject(AppInsightsService);
  private appId$ = this.store.select(selectAppId);
  private booking$ = this.store.select(selectBooking);
  private isFlightsPage$ = this.store.select(selectIsFlightsPage);
  private isFlowOrTripsPage$ = this.store.select(selectIsFlowOrTripsPage);
  private storageEvent$ = WindowService.storageEvent;
  private bookingStateChange = new Subject<void>();
  private notificationModal: BsModalRef<ConfirmationMessageModalComponent>;

  public isMobileAppInstalled = false;

  readonly N0_MULTIAPP_BOOKING_STATE = 'N0_MULTIAPP_BOOKING_STATE';
  readonly NO_BOOKING_IN_STATE = 'no_booking_in_state';

  constructor(@Inject(WINDOW) private readonly window: Window) {
    this.updateBookingState();
    this.triggerBookingStateChange();
    this.bookingStageChanged();
  }

  private updateBookingStateInStorage(value: string): void {
    this.localStorageService.setItem(this.N0_MULTIAPP_BOOKING_STATE, value);
  }

  get bookingStateChange$(): Observable<void> {
    return this.bookingStateChange.asObservable();
  }

  get notificationModalRef(): BsModalRef<ConfirmationMessageModalComponent> {
    return this.notificationModal;
  }

  private bookingStageChanged(): void {
    this.bookingStateChange$.pipe(
      concatLatestFrom(() => this.store.select(selectAppCmsCopy))
    ).subscribe(([,appCopy] : [void, AppCopy]) => {
      const modalConfig: NotificationModalConfig = {
        title: appCopy.multiAppModalTitle,
        message: appCopy.multiAppModalMsg,
        confirmBtnText: appCopy.multiAppModalConfirmBtn,
      };

      this.showBookingChangedNotification(modalConfig);
    });
  }

  private updateBookingState(): void {
    this.booking$
      .pipe(concatLatestFrom(() => [this.appId$, this.isFlowOrTripsPage$]))
      .subscribe(
        ([booking, appId, isFlowOrTripsPage]: [Booking, string, boolean]) => {
          if (booking && isFlowOrTripsPage) {
            if (
              this.gbs.growthBook.getFeatureValue('feat-001095-multiapp', false)
            ) {
              this.updateBookingStateInStorage(appId);
            }
          }
        },
      );
  }

  private triggerBookingStateChange(): void {
    this.storageEvent$
      .pipe(
        concatLatestFrom(() => [
          this.booking$,
          this.isFlightsPage$,
          this.isFlowOrTripsPage$,
        ]),
      )
      .subscribe(
        ([event, booking, isFlightsPage, isFlowOrTripsPage]: [
          StorageEvent,
          Booking,
          boolean,
          boolean,
        ]) => {
          if (event.key === this.N0_MULTIAPP_BOOKING_STATE) {
            if ((booking && isFlowOrTripsPage) || isFlightsPage) {
              if (
                this.gbs.growthBook.getFeatureValue(
                  'feat-001095-multiapp',
                  false,
                )
              ) {
                this.bookingStateChange.next();
              }
            }
          }
        },
      );
  }

  noBookingInState(): void {
    this.updateBookingStateInStorage(this.NO_BOOKING_IN_STATE);
  }

  showBookingChangedNotification(config: NotificationModalConfig): void {
    this.appInsights.logEvent('multiapp-notification-shown');

    this.router.navigate(['/'], { replaceUrl: true }).then(() => {
      this.modalService
        .show(ConfirmationMessageModalComponent, {
          class: MODAL_DIALOG_CENTER,
          initialState: {
            titleText: config.title,
            messageText: config.message,
            confirmBtnText: config.confirmBtnText,
          },
        })
        .onHidden?.pipe(take(1))
        .subscribe(() => {
          if (typeof config.confirmAction == 'function') {
            config.confirmAction();
          }
        });
    });
  }

  initMobileApp() {
    const url = this.window.location.href;
    if (url) {
      const searchParams = new URL(url).searchParams;

      const queryReferral = searchParams.get(
        MOBILE_APP_REFERRAL,
      );
      if (queryReferral) {
        this.localStorageService.setItem(
          MOBILE_APP_REFERRAL,
          queryReferral,
        );
      }

      const queryCallbackUrl = searchParams.get(
        MOBILE_APP_CALLBACK_URL,
      );
      if (queryCallbackUrl) {
        this.localStorageService.setItem(
          MOBILE_APP_CALLBACK_URL,
          queryCallbackUrl,
        );
      }
    }

    const referral = this.localStorageService.getItem<string | null>(
      MOBILE_APP_REFERRAL,
    );
    if (referral) {
      this.isMobileAppInstalled = true;
    }
  }

  openMobileApp(relativeUrl?: string): void {
    this.window.location.href = `${MOBILE_APP_SCHEME}://${relativeUrl ?? ''}`;
  }

  addBookingToMobileApp(pnr: string, lastname: string | undefined): void {
    if (lastname) {
      const addTripUrl = `/trip/add/${pnr}/${lastname}`;
      this.openMobileApp(addTripUrl);
    }
  }

  openMobileAppWithCallbackUrl() {
    const callbackUrl = this.localStorageService.getItem<string | null>(
      MOBILE_APP_CALLBACK_URL,
    );
    if (callbackUrl) {
      this.localStorageService.removeItem(MOBILE_APP_CALLBACK_URL);
      this.openMobileApp(callbackUrl);
    } else {
      this.openMobileApp();
    }
  }
}
